<template>
  <section class="flex h-full flex-col gap-3">
    <div class="rounded-lg border border-slate-200 bg-white p-3">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-slate-900">月度客户名单</h3>
          <p class="mt-0.5 text-xs text-slate-500">
            {{ plan?.planMonth || '-' }} · {{ regions.length }} 个区域 · {{ customers.length }} 个客户
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:region:manage'"
            :disabled="syncing"
            @click="emit('create-region')"
          >
            <PlusIcon class="h-4 w-4" />
            新增区域
          </button>
          <button
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:region:manage'"
            :disabled="syncing || regions.length < 2"
            @click="emit('sort-regions-by-amount')"
          >
            <ArrowsUpDownIcon class="h-4 w-4" />
            按金额排序
          </button>
          <button
            type="button"
            class="btn-primary inline-flex items-center gap-1.5"
            v-permission="'enterprise-invoice:list:confirm'"
            :disabled="syncing || regions.length === 0"
            @click="emit('confirm-list')"
          >
            <CheckCircleIcon class="h-4 w-4" />
            确认名单
          </button>
        </div>
      </div>
    </div>

    <div v-if="regions.length === 0" class="data-table-empty is-compact bg-white">
      暂无名单区域，点击右上角“新增区域”开始
    </div>

    <div v-else class="flex-1 min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div ref="matrixScrollRef" class="h-full overflow-auto">
        <div class="min-w-max border-l border-t border-slate-200">
          <div class="sticky top-0 z-10">
            <div class="grid" :style="gridStyle">
              <div
                v-for="(region, index) in regions"
                :key="region.id"
                :class="['min-h-[88px] border-r border-b border-slate-200 bg-slate-50 px-3 py-2.5', regionAccentClass(index)]"
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-semibold text-slate-900">{{ region.regionName || '未命名区域' }}</div>
                    <div class="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span class="text-[11px] uppercase tracking-wide text-slate-400">客户</span>
                      <span class="text-sm font-semibold text-slate-700">{{ regionCustomers(region.id).length }}</span>
                      <span class="text-[11px] uppercase tracking-wide text-slate-400">合计</span>
                      <span class="text-sm font-semibold text-blue-700">{{ formatWanAmount(invoiceRegionTotalAmount(region.id, customers)) }}</span>
                    </div>
                  </div>
                  <div class="flex shrink-0 gap-1">
                    <button
                      type="button"
                      class="table-action-button is-neutral"
                      v-permission="'enterprise-invoice:region:manage'"
                      title="上移"
                      :disabled="syncing || index === 0"
                      @click="moveRegion(index, -1)"
                    >
                      <ArrowUpIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="table-action-button is-neutral"
                      v-permission="'enterprise-invoice:region:manage'"
                      title="下移"
                      :disabled="syncing || index === regions.length - 1"
                      @click="moveRegion(index, 1)"
                    >
                      <ArrowDownIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="table-action-button is-primary"
                      v-permission="'enterprise-invoice:region:manage'"
                      title="重命名"
                      :disabled="syncing"
                      @click="emit('rename-region', region)"
                    >
                      <PencilSquareIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="table-action-button is-danger"
                      v-permission="'enterprise-invoice:region:manage'"
                      title="删除"
                      :disabled="syncing"
                      @click="requestDeleteRegion(region)"
                    >
                      <TrashIcon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-for="row in matrixRows" :key="row.rowIndex" class="grid" :style="gridStyle">
            <InvoiceMatrixCustomerCell
              v-for="cell in row.cells"
              :key="cell.key"
              :region="cell.region"
              :regions="regions"
              :customer="cell.customer"
              :row-index="cell.rowIndex"
              :dragging-customer-id="draggedCustomer?.id"
              :downstream-allocations="downstreamAllocations"
              :downstream-tag-preferences="downstreamTagPreferences"
              :syncing="syncing"
              @create-customer="emit('create-customer', $event)"
              @edit-customer="emit('edit-customer', $event)"
              @delete="emit('delete-customer', $event)"
              @drag-start="handleCustomerDragStart"
              @drag-leave="handleCustomerDragLeave"
              @drop-customer="handleCustomerDrop"
              @drag-end="handleCustomerDragEnd"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowsUpDownIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import { buildRegionMatrixRows, formatWanAmount, invoiceDropMovePayload, invoiceRegionTotalAmount } from '../utils'
import InvoiceMatrixCustomerCell from './InvoiceMatrixCustomerCell.vue'
import { useWheelToHorizontalScroll } from '../composables/useWheelToHorizontalScroll'

const props = defineProps({
  plan: { type: Object, default: null },
  regions: { type: Array, default: () => [] },
  customers: { type: Array, default: () => [] },
  customersByRegion: { type: Object, default: () => new Map() },
  downstreamAllocations: { type: Array, default: () => [] },
  downstreamTagPreferences: { type: Array, default: () => [] },
  syncing: { type: Boolean, default: false }
})

const emit = defineEmits([
  'create-region',
  'rename-region',
  'delete-region',
  'reorder-regions',
  'sort-regions-by-amount',
  'create-customer',
  'edit-customer',
  'move-customer',
  'delete-customer',
  'confirm-list'
])

const matrixScrollRef = useWheelToHorizontalScroll()

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${Math.max(props.regions.length, 1)}, minmax(168px, 1fr))`
}))
const matrixRows = computed(() => buildRegionMatrixRows(props.regions, props.customers, 4))
const draggedCustomer = ref(null)

const regionAccentClasses = [
  'border-t-2 border-t-blue-400',
  'border-t-2 border-t-emerald-400',
  'border-t-2 border-t-amber-400',
  'border-t-2 border-t-violet-400',
  'border-t-2 border-t-rose-400',
  'border-t-2 border-t-cyan-400'
]
function regionAccentClass(index) {
  return regionAccentClasses[index % regionAccentClasses.length]
}

function regionCustomers(regionId) {
  if (props.customersByRegion instanceof Map) return props.customersByRegion.get(regionId) || []
  return props.customersByRegion?.[regionId] || []
}

function moveRegion(index, offset) {
  const nextIndex = index + offset
  if (nextIndex < 0 || nextIndex >= props.regions.length) return
  const nextRegions = [...props.regions]
  const current = nextRegions[index]
  nextRegions[index] = nextRegions[nextIndex]
  nextRegions[nextIndex] = current
  emit('reorder-regions', nextRegions)
}

function requestDeleteRegion(region) {
  if (regionCustomers(region.id).length > 0) {
    ElMessage.warning('名单区域下存在客户，不能删除')
    return
  }
  emit('delete-region', region)
}
function handleCustomerDragStart(payload) {
  draggedCustomer.value = payload?.customer || null
}

function handleCustomerDragLeave() {
}

function handleCustomerDragEnd() {
  draggedCustomer.value = null
}

function handleCustomerDrop(payload) {
  const source = draggedCustomer.value
  const targetRegionId = payload?.region?.id
  if (!source?.id || !targetRegionId) {
    draggedCustomer.value = null
    return
  }
  const movePayload = invoiceDropMovePayload({
    sourceCustomer: source,
    targetRegionId,
    targetCustomers: regionCustomers(targetRegionId),
    droppedCustomer: payload.customer,
    position: payload.position,
    rowIndex: payload.rowIndex
  })
  if (!movePayload) {
    draggedCustomer.value = null
    return
  }
  emit('move-customer', movePayload)
  draggedCustomer.value = null
}
</script>
